import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { VoiceActivityDraft } from '@/api/apis/VoiceActivityApi';
import ActivityEditDialog from './ActivityEditDialog';

type Segment = VoiceActivityDraft['segment'];

const SEGMENT_ORDER: Segment[] = ['MORNING', 'AFTERNOON', 'EVENING', 'NIGHT'];

const SEGMENT_LABELS: Record<Segment, string> = {
  MORNING: '☀️ Morgon',
  AFTERNOON: '🌤 Eftermiddag',
  EVENING: '🌇 Kväll',
  NIGHT: '🌙 Natt',
};

type DayScheduleViewProps = {
  activities: VoiceActivityDraft[];
  date: Date;
  onEdit: (index: number, updated: VoiceActivityDraft) => void;
  onDelete: (index: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
  /** Barnläge: större touch-targets, rundare hörn, större text. Default false. */
  child?: boolean;
};

const DayScheduleView = ({
  activities,
  onEdit,
  onDelete,
  onConfirm,
  onCancel,
  saving,
  child = false,
}: DayScheduleViewProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // cv = "child value": välj barnvärde om child, annars oförändrat vuxenvärde.
  const cv = <T,>(childValue: T, adultValue: T): T => (child ? childValue : adultValue);

  const grouped = SEGMENT_ORDER.reduce<
    Record<Segment, Array<{ activity: VoiceActivityDraft; originalIndex: number }>>
  >(
    (acc, seg) => {
      acc[seg] = activities
        .map((activity, originalIndex) => ({ activity, originalIndex }))
        .filter(({ activity }) => activity.segment === seg);
      return acc;
    },
    { MORNING: [], AFTERNOON: [], EVENING: [], NIGHT: [] },
  );

  const editingActivity = editingIndex !== null ? activities[editingIndex] ?? null : null;

  return (
    <Stack spacing={0}>
      <Typography
        variant="h6"
        fontWeight={cv(700, 600)}
        pb={1}
        sx={child ? { fontSize: 20 } : undefined}
      >
        {cv('Så här blir din dag!', 'Ditt föreslagna schema')}
      </Typography>

      {SEGMENT_ORDER.map((seg) => {
        const items = grouped[seg];
        if (items.length === 0) return null;
        return (
          <Box key={seg} mb={2}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 0.5,
                ...(child && { fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }),
              }}
            >
              {SEGMENT_LABELS[seg]}
            </Typography>
            <Stack spacing={1}>
              {items.map(({ activity, originalIndex }) => (
                <Box
                  key={originalIndex}
                  sx={{
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    pl: cv(2, 1.5),
                    py: cv(1.5, 1),
                    pr: 1,
                    borderRadius: cv('0 14px 14px 0', '0 8px 8px 0'),
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      sx={child ? { fontSize: 17 } : undefined}
                    >
                      {activity.activityName}
                    </Typography>
                    {/* A11y: i barnläge höjs tid/duration till 14px (caption ~12px
                        bryter 14px-minimum). */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={child ? { fontSize: 14 } : undefined}
                    >
                      {activity.startTime} &bull; {activity.duration} min
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0}>
                    <IconButton
                      size={cv('medium', 'small')}
                      onClick={() => setEditingIndex(originalIndex)}
                      aria-label={`Redigera ${activity.activityName}`}
                    >
                      <EditIcon
                        sx={child ? { fontSize: 22 } : undefined}
                        fontSize={cv('medium', 'small')}
                      />
                    </IconButton>
                    <IconButton
                      size={cv('medium', 'small')}
                      onClick={() => onDelete(originalIndex)}
                      aria-label={`Ta bort ${activity.activityName}`}
                    >
                      <DeleteIcon
                        sx={child ? { fontSize: 22 } : undefined}
                        fontSize={cv('medium', 'small')}
                      />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}

      <Divider sx={{ my: 1 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        fontStyle="italic"
        textAlign="center"
        sx={child ? { fontSize: 14 } : undefined}
      >
        Ingenting sparas förrän du trycker Lägg till
      </Typography>

      <Stack direction="row" spacing={2} pt={2}>
        <Button
          variant="outlined"
          onClick={onCancel}
          fullWidth
          disabled={saving}
          sx={
            child
              ? { minHeight: 52, borderRadius: '14px', fontSize: 16, fontWeight: 700 }
              : undefined
          }
        >
          Avbryt
        </Button>
        {/* Spara-knappen förblir primary.main (blå) — grön är reserverad för
            "klar"-status, inte för bekräfta-handlingen. */}
        <Button
          variant="contained"
          onClick={onConfirm}
          fullWidth
          disabled={saving || activities.length === 0}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          sx={
            child
              ? { minHeight: 52, borderRadius: '14px', fontSize: 16, fontWeight: 700 }
              : undefined
          }
        >
          {saving
            ? 'Sparar...'
            : `Lägg till ${activities.length} aktivitet${activities.length !== 1 ? 'er' : ''}`}
        </Button>
      </Stack>

      <ActivityEditDialog
        open={editingIndex !== null}
        activity={editingActivity}
        onSave={(updated) => {
          if (editingIndex !== null) {
            onEdit(editingIndex, updated);
          }
          setEditingIndex(null);
        }}
        onClose={() => setEditingIndex(null)}
        child={child}
      />
    </Stack>
  );
};

export default DayScheduleView;
